import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useApiSWR } from "@/hooks/useApiSWR";
import { cn } from "@/lib/utils";
import { listInboundEmails } from "@/services/inbound-email.service";
import { INBOX_TABS, IGNORE_REASON_LABEL, formatEmailDate } from "./status";

export function InboxList({
	tab,
	onTabChange,
	selectedId,
}: {
	tab: string;
	onTabChange: (tab: string) => void;
	selectedId: string | undefined;
}) {
	const active = INBOX_TABS.find((t) => t.key === tab) ?? INBOX_TABS[0];
	const { data: emails, error, isLoading, mutate } = useApiSWR(
		["inbound-emails", active.key],
		() => listInboundEmails(active.statuses),
		{ refreshInterval: 30_000 },
	);

	return (
		<div className="rounded-xl border border-border bg-surface">
			<div role="tablist" aria-label="Inbox status" className="flex gap-1 overflow-x-auto border-b border-border p-2">
				{INBOX_TABS.map((t) => (
					<button
						key={t.key}
						role="tab"
						type="button"
						aria-selected={t.key === active.key}
						onClick={() => onTabChange(t.key)}
						className={cn(
							"shrink-0 rounded-md px-2.5 py-1.5 text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
							t.key === active.key
								? "bg-primary-subtle font-medium text-primary"
								: "text-muted-foreground hover:bg-muted-subtle hover:text-foreground",
						)}
					>
						{t.label}
					</button>
				))}
			</div>

			<div role="tabpanel" aria-label={`${active.label} emails`}>
				{error ? (
					<div className="p-4">
						<p className="text-sm text-danger">{error.message || "Failed to load emails."}</p>
						<Button size="sm" variant="outline" className="mt-3" onClick={() => mutate()}>
							Retry
						</Button>
					</div>
				) : isLoading ? (
					<div className="space-y-2 p-3" aria-busy="true">
						{[0, 1, 2].map((i) => (
							<Skeleton key={i} className="h-14 w-full rounded-lg" />
						))}
					</div>
				) : !emails?.length ? (
					<p className="p-6 text-center text-sm text-muted">
						{active.key === "ready"
							? "Nothing to review. Emails sent to the TaskHub address show up here once the agent has drafted tasks."
							: "No emails here."}
					</p>
				) : (
					<ul className="divide-y divide-border">
						{emails.map((e) => (
							<li key={e.id}>
								<Link
									to={`/inbox/${e.id}?tab=${active.key}`}
									aria-current={e.id === selectedId ? "page" : undefined}
									className={cn(
										"block px-4 py-3 transition-colors hover:bg-muted-subtle focus:outline-none focus-visible:bg-muted-subtle",
										e.id === selectedId && "bg-primary-subtle/50",
									)}
								>
									<div className="flex items-baseline justify-between gap-2">
										<p className="truncate text-sm font-medium text-foreground">{e.from_name || e.from_email}</p>
										<time className="shrink-0 text-xs text-muted" dateTime={e.created_at}>
											{formatEmailDate(e.created_at)}
										</time>
									</div>
									<p className="truncate text-sm text-muted-foreground">{e.subject || "(no subject)"}</p>
									{e.status === "ignored" && e.ignore_reason && (
										<p className="truncate text-xs text-muted">
											{IGNORE_REASON_LABEL[e.ignore_reason] ?? e.ignore_reason}
										</p>
									)}
									{e.status === "failed" && e.error && <p className="truncate text-xs text-danger">{e.error}</p>}
									{e.status === "approving" && <p className="text-xs text-primary">Approval in progress</p>}
								</Link>
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	);
}
